"use client";

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/auth/use-auth";
import {
  sanitizeVoiceAgentConfig,
  type VoiceAgentConfig,
  type VoiceAgentAiToolsConfig,
  type VoiceAgentKBSyncMapping,
  type VoiceAgentRagSettings,
} from "@/lib/voice/agent-config";

export type AIToolsConfig = VoiceAgentAiToolsConfig;
export type KBSyncMapping = VoiceAgentKBSyncMapping;
export type RagSettings = VoiceAgentRagSettings;
export type { VoiceAgentConfig };

export interface KBSyncResult {
  success: boolean;
  message: string;
  synced: KBSyncMapping[];
  removed: number;
  errors?: string[];
}

export interface KBSyncStatus {
  agent_id: string;  // UUID
  agent_name: string;
  has_external_id: boolean;
  selected_count: number;
  synced_count: number;
  needs_sync: boolean;
  needs_sync_count: number;
  synced_documents: KBSyncMapping[];
}

export interface VoiceAgentConfigResponse {
  agent_id: string;  // UUID
  agent_name: string;
  config: VoiceAgentConfig;
}

export function useVoiceAgentConfig(agentId: string | null) {
  const [config, setConfig] = useState<VoiceAgentConfig>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<KBSyncStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithAuth, isAuthenticated } = useAuth();

  const loadConfig = useCallback(async () => {
    if (!isAuthenticated || !agentId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `/api/agents/voice/${agentId}/config`,
      );

      if (response.ok) {
        const data: VoiceAgentConfigResponse = await response.json();
        // Sanitize config to ensure deprecated fields are stripped
        setConfig(sanitizeVoiceAgentConfig(data.config));
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to load config");
      }
    } catch (err) {
      console.error("Error loading voice agent config:", err);
      setError("Failed to load config");
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAuthenticated, agentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = useCallback(
    async (newConfig: Partial<VoiceAgentConfig>): Promise<boolean> => {
      if (!agentId) return false;

      setIsSaving(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `/api/agents/voice/${agentId}/config`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ config: newConfig }),
          },
        );

        if (response.ok) {
          const data: VoiceAgentConfigResponse = await response.json();
          setConfig(data.config || {});
          return true;
        } else {
          const errorData = await response.json();
          setError(errorData.error || "Failed to save config");
          return false;
        }
      } catch (err) {
        console.error("Error saving voice agent config:", err);
        setError("Failed to save config");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [fetchWithAuth, agentId],
  );

  const updateKnowledgeBases = useCallback((kbIds: string[]) => {
    setConfig((prev) => ({
      ...prev,
      knowledge_bases: kbIds,
    }));
  }, []);

  const toggleKnowledgeBase = useCallback((kbId: string) => {
    setConfig((prev) => {
      const current = prev.knowledge_bases || [];
      const newKbs = current.includes(kbId)
        ? current.filter((id) => id !== kbId)
        : [...current, kbId];
      return {
        ...prev,
        knowledge_bases: newKbs,
      };
    });
  }, []);

  const updateRagSettings = useCallback(
    (updates: Partial<RagSettings>) => {
      setConfig((prev) => ({
        ...prev,
        rag_settings: {
          ...prev.rag_settings,
          ...updates,
        },
      }));
    },
    []
  );

  const syncKnowledgeBase = useCallback(async (): Promise<KBSyncResult | null> => {
    if (!agentId) return null;

    setIsSyncing(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `/api/agents/voice/${agentId}/sync-kb`,
        {
          method: "POST",
        }
      );

      const data = await response.json();

      if (response.ok) {
        setConfig((prev) => ({
          ...prev,
          knowledge_base_sync: data.synced,
        }));
        return data as KBSyncResult;
      } else {
        setError(data.error || "Failed to sync KB");
        return null;
      }
    } catch (err) {
      console.error("Error syncing KB:", err);
      setError("Failed to sync KB");
      return null;
    } finally {
      setIsSyncing(false);
    }
  }, [fetchWithAuth, agentId]);

  const loadSyncStatus = useCallback(async () => {
    if (!agentId) return;

    try {
      const response = await fetchWithAuth(
        `/api/agents/voice/${agentId}/sync-kb`
      );

      if (response.ok) {
        const data: KBSyncStatus = await response.json();
        setSyncStatus(data);
      }
    } catch (err) {
      console.error("Error loading sync status:", err);
    }
  }, [fetchWithAuth, agentId]);

  const needsKBSync = useCallback(() => {
    const selectedKBs = config.knowledge_bases || [];
    const syncedKBs = config.knowledge_base_sync || [];
    const syncedIds = new Set(syncedKBs.map((s) => s.local_id));
    return selectedKBs.some((id) => !syncedIds.has(id));
  }, [config]);

  return {
    config,
    isLoading,
    isSaving,
    isSyncing,
    syncStatus,
    error,
    saveConfig,
    updateKnowledgeBases,
    toggleKnowledgeBase,
    updateRagSettings,
    refreshConfig: loadConfig,
    syncKnowledgeBase,
    loadSyncStatus,
    needsKBSync,
  };
}
